/**
 * FE-4577 — the `Fds` delivery-negotiation codec.
 *
 * Pure: no browser state, no I/O, no imports from the app. That is deliberate —
 * the hash has published self-check vectors (below) and this file can be pasted
 * into a devtools console to check them against a live backend.
 *
 * One format travels in both directions, so one parser serves both:
 *
 *   request    Fds: h=9f2c8b…; t=1756742400123; th=dark,light
 *   response   Fds: t=1756750100789
 *   response   Fds: t=1756750100789; r=1
 *
 * `h` is the fingerprint of what the client holds; the backend NEVER sends it
 * back. The client computes it itself — two independent computations disagreeing
 * is precisely how a half-lost store is detected.
 *
 * The same string also travels in the body as `session_data.fds`, because no
 * `F*` header appears in any `Access-Control-Expose-Headers` list, so a
 * cross-origin response has its header stripped while the body field survives.
 */

/** What an empty or unusable store sends. Also the fallback when SHA-256 is unavailable. */
export const FDS_NONE = 'none';

export type FdsHashInput = {
  classIds: string[];
  templateIds: string[];
  themeNames: string[];
  hasConfig: boolean;
};

export type FdsResponse = {
  /** The delivery mark. Opaque — stored and returned verbatim. */
  mark: string | null;
  /** `r=1` — drop everything held, then store what arrived. Not a merge. */
  reset: boolean;
};

/**
 * Orders by Unicode code point, which is also UTF-8 byte order — what Go's
 * `sort.Strings` produces on the other side.
 *
 * A bare `.sort()` compares UTF-16 code units, which disagrees for
 * supplementary-plane characters (a surrogate pair sorts below U+E000 there,
 * above it here). External ids are almost certainly ASCII, where the two agree,
 * so this is insurance: getting it wrong yields a permanent hash mismatch, i.e.
 * a permanent full delivery — degraded, never incorrect.
 */
const compareByCodePoint = (a: string, b: string): number => {
  const left = Array.from(a).map((ch) => ch.codePointAt(0) ?? 0);
  const right = Array.from(b).map((ch) => ch.codePointAt(0) ?? 0);
  const diffAt = left.findIndex((cp, i) => cp !== right[i]);
  if (diffAt === -1) return left.length - right.length;
  if (diffAt >= right.length) return 1;
  return left[diffAt] - right[diffAt];
};

const joinSorted = (ids: string[]): string => [...ids].sort(compareByCodePoint).join(',');

/**
 * The exact bytes the hash is taken over.
 *
 * Verified against the backend's published vectors (real `\n`, no trailing
 * newline, an empty list contributes an empty segment so the separators are
 * always present):
 *
 *   c:\nt:\ns:\ng:0                     → f2e16814b01def945b24f384ce07aa6f1be902c2591f50060d1cc8b1ad7af2a6
 *   c:cl-1\nt:\ns:\ng:0                 → b52da51bca1afc919bfd6dd0c2e100b7aa1ed6291598ae2b07c59ddcefb1bac0
 *   c:cl-1,cl-2\nt:tpl-1\ns:dark\ng:1   → e9e04108b5ad5e81ad4be1aa14195e754da2f2d4ef5caee0eeb2c009f96d8d1b
 *
 * NOTE the FE-4577 ticket description states an older formula (hashing the
 * styles and config BODIES). That is stale. `s:` is theme NAMES and `g:` is a
 * config PRESENCE flag — the bodies are not hashed at all, which is why editing
 * a class body leaves the hash identical and why the `t` mark has to exist.
 */
export const buildFdsPreimage = ({ classIds, templateIds, themeNames, hasConfig }: FdsHashInput): string =>
  `c:${joinSorted(classIds)}\n`
  + `t:${joinSorted(templateIds)}\n`
  + `s:${joinSorted(themeNames)}\n`
  + `g:${hasConfig ? '1' : '0'}`;

/**
 * SHA-256 as lowercase hex, or `null` when it cannot be computed.
 *
 * `crypto.subtle` is secure-context-only, so a page served over plain http on a
 * LAN address has no digest available. The caller turns `null` into `h=none`,
 * which is simply today's behaviour: the backend delivers everything.
 */
export const sha256Hex = async (input: string): Promise<string | null> => {
  try {
    const subtle = globalThis.crypto?.subtle;
    if (!subtle) return null;
    const digest = await subtle.digest('SHA-256', new TextEncoder().encode(input));
    return Array.from(new Uint8Array(digest))
      .map((byte) => byte.toString(16).padStart(2, '0'))
      .join('');
  } catch (error) {
    return null;
  }
};

export const computeFdsHash = async (input: FdsHashInput): Promise<string> =>
  (await sha256Hex(buildFdsPreimage(input))) ?? FDS_NONE;

/**
 * Splits an `Fds` string into its fields.
 *
 * ABSENT IS NOT EMPTY, and the distinction carries two protocol rules:
 *   `null` — no `Fds` at all ⇒ an older backend, or the SSR shell. The store
 *            must be left completely untouched (rules 2 and 8).
 *   `{}`   — present but empty ⇒ still a delivery; the mark is stored (rule 1).
 *
 * Splits each field on the FIRST `=` only, since a mark could itself contain one.
 */
export const parseFds = (value: unknown): Record<string, string> | null => {
  if (typeof value !== 'string') return null;
  const parts = value.split(';').map((part) => part.trim()).filter(Boolean);
  return Object.fromEntries(parts.map((part) => {
    const eq = part.indexOf('=');
    return eq === -1
      ? [part, '']
      : [part.slice(0, eq).trim(), part.slice(eq + 1).trim()];
  }));
};

export const readFdsResponse = (value: unknown): FdsResponse | null => {
  const parsed = parseFds(value);
  if (!parsed) return null;
  return { mark: parsed.t || null, reset: parsed.r === '1' };
};

export const serializeFdsRequest = (
  { hash, mark, themes }: { hash: string; mark: string | null; themes: string[] },
): string => {
  const parts = [`h=${hash || FDS_NONE}`];
  if (mark) parts.push(`t=${mark}`);
  if (themes.length > 0) parts.push(`th=${joinSorted(themes)}`);
  return parts.join('; ');
};

/**
 * Body first, header second.
 *
 * `session_data.fds` always survives; the header does not, cross-origin. Both
 * envelope depths are probed because `session_data` sits at the root of the flat
 * 0.22.x envelope but under `data` in the legacy one.
 */
export const fdsOf = (payload: any, headerValue?: string | null): string | null => {
  const fromRoot = payload?.session_data?.fds;
  if (typeof fromRoot === 'string') return fromRoot;
  const fromData = payload?.data?.session_data?.fds;
  if (typeof fromData === 'string') return fromData;
  return typeof headerValue === 'string' ? headerValue : null;
};
